// Projects (/projects) — getAaronProjects (BrainProject[]) → KPI row + the shared
// <ProjectsTable> (mui/ProjectsTable), the same table the standup Projects tab renders.
// New-project dialog: saveProject → navigate to the detail page's overview tab.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { getAaronProjects, saveProject, setProjectFocus, listDepartmentsApi, type BrainProject } from "../../lib/brain-api";
import { useProfile } from "../../lib/profile";
import { brainTokens } from "../../design/brainTheme";
import { KpiCard, PageHeader, EmptyState, Icon, ProjectsTable, matchProjectSeg } from "../../mui";

const c = brainTokens.color;

export function ProjectsPageMui() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { profile } = useProfile();
  const isAdmin = profile?.role === "admin";
  const [projects, setProjects] = useState<BrainProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newOwner, setNewOwner] = useState("");
  const [newGoal, setNewGoal] = useState("");
  const [newTeam, setNewTeam] = useState("");
  const [newEpic, setNewEpic] = useState("");
  const [deptOptions, setDeptOptions] = useState<Array<{ id: string; label: string }>>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void listDepartmentsApi()
      .then((rows) => setDeptOptions(rows.map((d) => ({ id: d.deptId, label: d.label }))))
      .catch(() => undefined);
  }, []);

  const activationMissing = useMemo(() => {
    const missing: string[] = [];
    if (!newOwner.trim()) missing.push("owner");
    if (!newGoal.trim()) missing.push("goal");
    if (!newTeam.trim()) missing.push("department");
    if (!newEpic.trim()) missing.push("Jira epic");
    return missing;
  }, [newOwner, newGoal, newTeam, newEpic]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Defensive: hide archived even if talking to an API that predates the
      // server-side archived filter in listBrainProjects (keeps the two surfaces aligned).
      setProjects((await getAaronProjects({})).filter((p) => p.status !== "archived"));
    } catch (e) {
      const m = e instanceof Error ? e.message : "Failed to load projects.";
      setError(m);
      enqueueSnackbar(m, { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);
  useEffect(() => {
    void load();
  }, [load]);

  const createProject = async () => {
    const name = newName.trim();
    if (!name || activationMissing.length) return;
    const projectId = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    setBusy(true);
    try {
      await saveProject({
        projectId,
        name,
        kind: "internal",
        customer: "aeronet",
        status: "planning",
        owner: newOwner.trim(),
        description: newGoal.trim(),
        team: newTeam.trim(),
        links: { jira_epic: newEpic.trim(), repos: [] },
      });
      enqueueSnackbar("Project created.", { variant: "success" });
      setNewOpen(false);
      setNewName("");
      setNewOwner("");
      setNewGoal("");
      setNewTeam("");
      setNewEpic("");
      navigate(`/projects/${encodeURIComponent(projectId)}?tab=overview`);
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Create failed.", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  // Star in the table → escalate/demote a project on the standup focus list.
  // Optimistic: the row flips instantly, and rolls back if the write fails.
  const toggleFocus = useCallback(
    (projectId: string, next: boolean) => {
      setProjects((prev) => prev.map((p) => (p.projectId === projectId ? { ...p, isFocus: next } : p)));
      void setProjectFocus(projectId, next).catch((e) => {
        setProjects((prev) => prev.map((p) => (p.projectId === projectId ? { ...p, isFocus: !next } : p)));
        enqueueSnackbar(e instanceof Error ? e.message : "Failed to change focus.", { variant: "error" });
      });
    },
    [enqueueSnackbar],
  );

  const kpis = useMemo(() => {
    const total = projects.length;
    const focus = projects.filter((p) => p.isFocus).length;
    const attention = projects.filter((p) => matchProjectSeg(p.status, "atrisk") || matchProjectSeg(p.status, "blocked")).length;
    const shipped = projects.filter((p) => matchProjectSeg(p.status, "shipped")).length;
    return { total, focus, attention, shipped };
  }, [projects]);

  return (
    <Box>
      <PageHeader
        title="Projects"
        eyebrow="Every project across all customers and internal teams"
        actions={
          isAdmin && (
            <Button variant="contained" onClick={() => setNewOpen(true)} startIcon={<Icon name="plus" size={15} stroke={2} />}>
              New project
            </Button>
          )
        }
      />

      {error ? (
        <Box sx={{ mt: 3 }}>
          <EmptyState icon="warning" title="Couldn't load projects">
            <Box sx={{ fontSize: 12, color: c.faint, mb: "12px" }}>{error}</Box>
            <Button variant="contained" onClick={() => void load()} startIcon={<Icon name="refresh" size={14} />}>
              Retry
            </Button>
          </EmptyState>
        </Box>
      ) : (
        <>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "14px", mt: 3, mb: "20px" }}>
            <KpiCard label="Projects" value={kpis.total} />
            <KpiCard label="Focus" value={kpis.focus} valueColor={c.tealInk2} hint="starred as current priority" />
            <KpiCard label="Needs attention" value={kpis.attention} valueColor={kpis.attention ? c.warnText : c.ink} hint="at risk or blocked" />
            <KpiCard label="Shipped" value={kpis.shipped} valueColor={c.successText} />
          </Box>

          <ProjectsTable
            projects={projects}
            loading={loading}
            canFocus={isAdmin}
            onToggleFocus={toggleFocus}
          />
        </>
      )}

      <Dialog open={newOpen} onClose={() => setNewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New project</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            label="Name"
            value={newName}
            placeholder="Inwards-goods rewrite"
            onChange={(e) => setNewName(e.target.value)}
            sx={{ mt: 1 }}
          />
          <TextField
            fullWidth
            label="Owner"
            value={newOwner}
            placeholder="isaac"
            onChange={(e) => setNewOwner(e.target.value)}
            helperText="Who owns this — required to activate."
          />
          <TextField
            fullWidth
            label="One-line goal"
            value={newGoal}
            placeholder="Ship X so customers can Y"
            onChange={(e) => setNewGoal(e.target.value)}
          />
          <TextField
            select
            fullWidth
            label="Department"
            value={newTeam}
            onChange={(e) => setNewTeam(e.target.value)}
          >
            <MenuItem value="">Select…</MenuItem>
            {deptOptions.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                {d.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            label="Jira epic"
            value={newEpic}
            placeholder="DO-1234"
            onChange={(e) => setNewEpic(e.target.value)}
            helperText="Epic key, or add plan.md after create before going active."
          />
          {activationMissing.length > 0 && newName.trim() && (
            <Box sx={{ fontSize: 12.5, color: c.warnText }}>
              Still need: {activationMissing.join(", ")}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewOpen(false)} sx={{ color: c.ink3 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={busy || !newName.trim() || activationMissing.length > 0}
            onClick={() => void createProject()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
